// src/components/MemberRenewContainer/MemberRenewContainer.jsx

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { 
  useCreateMemberEnrollmentRecordMutation,
  useCreateMemberMutation,
  useFetchMembersQuery, 
  useGetAdministrativeDivisionsQuery,
  useUpdateMemberMutation,
} from 'store/api/baseApiSlice';
import NewLayoutWithHeader from 'components/layouts/new-layout-with-header';
import { camelCaseObject } from 'lib/utils';

// Import child components
import SidePanel from './SidePanel';
import DependentsSection from './DependentsSection';
import AlertMessage from './AlertMessage';
import HouseholdHead from './HouseholdHead';
import MemberFormModal from './MemberFormModal';
import ErrorAlert, { SuccessAlert } from 'containers/member-import/ErrorAlert';
import { lowerCase } from 'lodash';

/**
 * Utility Function: Extract UUID from URL
 */
const useUUIDFromURL = () => {
  const [uuid, setUUID] = useState(null);

  useEffect(() => {
    const parts = window.location.href.split('/');
    setUUID(parts[parts.length - 2] || null);
  }, []);

  return uuid;
};

export const isEligibleToRegister = member => {
  const relationship = lowerCase(member.relationship_to_head);
  const profession = lowerCase(member.profession);
  const isDisabled = profession === 'disabled'
  const isMinorChild = member.age < 18 && (relationship === 'son' || relationship === 'daughter');
  const isSpouseOrSelf = relationship === 'husband' || relationship === 'wife' || relationship === 'self';
  if (isSpouseOrSelf || isMinorChild || isDisabled) {
    return true;
  }
  return false;
};

/**
 * Utility Functions: Create FormData for Member Operations
 */
const createMemberFormData = (memberData, householdId) => {
  const formData = new FormData();
  formData.append('full_name', memberData.fullName);
  formData.append('birthdate', memberData.birthdate);
  formData.append('gender', memberData.gender);
  formData.append('local_membership_number', memberData.localMembershipNumber);
  formData.append('national_id', memberData.nationalId);
  formData.append('membership_type', memberData.membershipType);
  formData.append('profession', memberData.profession);
  formData.append('relationship_to_head', memberData.relationshipToHead);
  formData.append('household_id', householdId);
  formData.append('enrolled_at', new Date().toISOString());
  formData.append('birthdate_accuracy', 'Y');

  if (memberData.photo) {
    formData.append('photo', memberData.photo);
  }

  return formData;
};

const updateMemberFormData = (memberData, householdId) => {
  const formData = new FormData();
  formData.append('id', memberData.id);
  formData.append('full_name', memberData.fullName);
  formData.append('birthdate', memberData.birthdate);
  formData.append('gender', memberData.gender);
  formData.append('local_membership_number', memberData.localMembershipNumber);
  formData.append('national_id', memberData.nationalId);
  formData.append('membership_type', memberData.membershipType);
  formData.append('profession', memberData.profession);
  formData.append('relationship_to_head', memberData.relationshipToHead);
  formData.append('household_id', householdId);
  formData.append('enrolled_at', new Date().toISOString());
  formData.append('birthdate_accuracy', 'Y');

  if (memberData.photo) {
    formData.append('photo', memberData.photo);
  }

  return formData;
};

/**
 * Component: Centered Modal for Alerts using React Portal
 */
export const CenteredModal = ({ children, onClose, type = 'info', title }) => {
  // Ensure modal-root exists
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  // Determine header colors based on type
  const getHeaderStyles = () => {
    switch (type) {
      case 'alert':
      case 'error':
      case 'danger':
        return {
          bg: 'tw-bg-gradient-to-r tw-from-red-500 tw-to-red-600',
          icon: (
            <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case 'success':
        return {
          bg: 'tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600',
          icon: (
            <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case 'info':
      default:
        return {
          bg: 'tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600',
          icon: (
            <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
    }
  };

  const headerStyles = getHeaderStyles();
  const titleText = title || (type === 'alert' || type === 'error' || type === 'danger' ? 'Error' : type === 'success' ? 'Success' : 'Information');

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-60 tw-backdrop-blur-sm tw-z-50 tw-p-4">
      <div className="tw-relative tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-md tw-w-full tw-overflow-hidden tw-border tw-border-gray-200">
        {/* Header with colored background */}
        <div className={`${headerStyles.bg} tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between`}>
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-bg-white tw-bg-opacity-20 tw-p-1.5 tw-rounded-lg">
              {headerStyles.icon}
            </div>
            <h3 className="tw-text-sm tw-font-semibold tw-text-white">{titleText}</h3>
          </div>
          <button
            onClick={onClose}
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-white tw-bg-opacity-20 tw-text-white hover:tw-bg-opacity-30 tw-transition-all tw-duration-200 tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-white tw-focus:ring-offset-2 tw-focus:ring-offset-transparent"
            aria-label="Close Modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="tw-h-4 tw-w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="tw-p-6">
          {children}
        </div>
      </div>
    </div>,
    modalRoot
  );
};

/**
 * Component: Confirm Dialog Modal
 */
export const ConfirmDialog = ({ isOpen, onClose, onConfirm, title = 'Confirm Action', message = 'Are you sure you want to proceed?', confirmText = 'Confirm', cancelText = 'Cancel', isLoading = false }) => {
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot || !isOpen) return null;

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-60 tw-backdrop-blur-sm tw-z-50 tw-p-4">
      <div className="tw-relative tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-md tw-w-full tw-overflow-hidden tw-border tw-border-gray-200">
        {/* Header */}
        <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-bg-white tw-bg-opacity-20 tw-p-1.5 tw-rounded-lg">
              <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="tw-text-sm tw-font-semibold tw-text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-white tw-bg-opacity-20 tw-text-white hover:tw-bg-opacity-30 tw-transition-all tw-duration-200 tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-white tw-focus:ring-offset-2 tw-focus:ring-offset-transparent"
            aria-label="Close Modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="tw-h-4 tw-w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="tw-p-6">
          <p className="tw-text-gray-700 tw-mb-6">{message}</p>
          
          {/* Action Buttons */}
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={`tw-px-5 tw-py-2.5 tw-bg-gray-100 tw-text-gray-700 tw-font-semibold tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-offset-2 tw-transition-all tw-duration-200 ${
                isLoading ? 'tw-opacity-50 tw-cursor-not-allowed' : 'hover:tw-bg-gray-200'
              }`}
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`tw-px-5 tw-py-2.5 tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-font-semibold tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-400 focus:tw-ring-offset-2 tw-transition-all tw-duration-200 tw-shadow-md tw-flex tw-items-center tw-gap-2 ${
                isLoading 
                  ? 'tw-opacity-50 tw-cursor-not-allowed' 
                  : 'hover:tw-from-blue-600 hover:tw-to-blue-700 hover:tw-shadow-lg'
              }`}
            >
              {isLoading && (
                <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>{confirmText}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};


/**
 * Component: Loading Indicator
 */
const LoadingIndicator = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-64">
    <div className="tw-loader tw-ease-linear tw-rounded-full tw-border-8 tw-border-t-8 tw-border-gray-200 tw-h-16 tw-w-16"></div>
    <style jsx>{`
      .tw-loader {
        border-top-color: #3498db;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * Main Component: MemberRenewContainer
 */
const MemberRenewContainer = () => {
  const uuid = useUUIDFromURL();

  // State for selectors
  const [selectedGote, setSelectedGote] = useState(null);
  const [selectedEnrollmentPeriod, setSelectedEnrollmentPeriod] = useState(null);
  const [createMemberEnrollmentRecord] = useCreateMemberEnrollmentRecordMutation();
  const [selectedPremium, setSelectedPremium] = useState(null);
  const [renewalProgress, setRenewalProgress] = useState({});
  const [selectedSecondWifeId, setSelectedSecondWifeId] = useState([]); // Changed to array to support multiple additional wives

  // Alert state
  const [alert, setAlert] = useState({ message: null, type: null });

  // Modal state for adding new member
  const [isAdding, setIsAdding] = useState(false);
  const [newMember, setNewMember] = useState({
    fullName: '',
    birthdate: '',
    gender: '',
    localMembershipNumber: '',
    nationalId: '',
    relationshipToHead: '',
    membershipType: '',
    profession: '',
    photo: null,
    id: null,
  });

  // RTK Query hooks
  const { data: administrativeDivisions = [], error: adminDivError, isLoading: isLoadingDivisions } = useGetAdministrativeDivisionsQuery();
  const [createMember] = useCreateMemberMutation();
  const [editMember] = useUpdateMemberMutation();

  const { data: membersData, isLoading, isError, error, refetch } = useFetchMembersQuery(uuid);
  const data = membersData ? membersData[0] : null;
  const householdEnrollmentRecord = data?.household_enrollment_record

  // State to manage dependents' renewal status
  const [dependents, setDependents] = useState([]);

  // Find the household head
  const householdHead = camelCaseObject(
    data?.members.find(
      (member) => member.relationship_to_head.toLowerCase() === 'self'
    ) || null
  );

  // Initialize dependents when data changes
  useEffect(() => {
    if (data && data.members) {
      const dependentsWithRenewal = data.members.map((member) => ({
        ...camelCaseObject(member),
        enabled: isEligibleToRegister(member)
      }));

      // Sorting logic for relationshipToHead: SELF first, then HUSBAND, WIFE, and others.
    const sortedDependents = dependentsWithRenewal.sort((a, b) => {
      const order = ['SELF', 'HUSBAND', 'WIFE'];
      const aIndex = order.indexOf(a.relationshipToHead);
      const bIndex = order.indexOf(b.relationshipToHead);
  
      if (aIndex === -1 && bIndex === -1) {
        // If both are not 'SELF', 'HUSBAND', or 'WIFE', keep their original order
        return 0;
      }
  
      // If only one is 'SELF', 'HUSBAND', or 'WIFE', prioritize that one
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
  
      // Compare based on their positions in the order array
      return aIndex - bIndex;
    });

      setDependents(sortedDependents);
    }
  }, [data]);

  /**
   * Handler to update a member's information
   */
  const handleUpdateMember = useCallback(
    async (updatedMember) => {
      try {
        const existingMember = dependents.find((dep) => dep.id === updatedMember.id);
        if (!existingMember) {
          setAlert({ message: 'Member not found.', type: 'error' });
          return;
        }

        // Use householdHead.householdId instead of existingMember.householdId
        const formData = updateMemberFormData(updatedMember, householdHead?.householdId || '');
        const member = await editMember(formData).unwrap();
        setAlert({ message: 'Member updated successfully.', type: 'success' });
        if (isEligibleToRegister(member) && householdEnrollmentRecord.enrollment_period_active && !member.active) {
          const memberEnrollmentData = {
            member_id: member.id,
            user_id: 456, // Replace with actual user ID
            enrollment_period_id: selectedEnrollmentPeriod.id,
            enrolled_at: new Date(),
          };
          await createMemberEnrollmentRecord(memberEnrollmentData).unwrap();
        }
        refetch()
          .then(() => {
            console.log('Members refetched successfully after update.');
          })
          .catch((error) => {
            console.error('Failed to refetch members after update:', error);
          });
      } catch (err) {
        console.error(err);
        setAlert({ message: 'Failed to update member.', type: 'error' });
      }
    },
    [dependents, editMember, householdHead, refetch, createMemberEnrollmentRecord]
  );

  /**
   * Handler to create a new member
   */
  const handleCreateMember = useCallback(
    async (memberData) => {
      try {
        const formData = createMemberFormData(memberData, householdHead?.householdId || '');
        const newMember = await createMember(formData).unwrap();
        
        // Enrollment logic happens first, if eligible
        if (isEligibleToRegister(newMember) && householdEnrollmentRecord?.enrollment_period_active) {
          const memberEnrollmentData = {
            member_id: newMember.id,
            user_id: 456, // Replace with actual user ID
            enrollment_period_id: selectedEnrollmentPeriod.id,
            enrolled_at: new Date(),
          };
          await createMemberEnrollmentRecord(memberEnrollmentData).unwrap(); // Enrollment logic
        }
        refetch()
          .then(() => {
            console.log('Members refetched successfully after update.');
          })
          .catch((error) => {
            console.error('Failed to refetch members after update:', error);
          });


        setIsAdding(false);
        setAlert({ message: 'New member added successfully.', type: 'success' });

      } catch (err) {
        console.error('Error creating new member:', err);
        setAlert({ message: 'Failed to add new member.', type: 'error' });
      }
    },
    [dependents, createMember, householdHead, refetch, createMemberEnrollmentRecord]
  );





  /**
   * Handler to add a new member
   */
  const handleAddMember = () => {
    setIsAdding(true);
  };

  /**
   * Handler for form field changes in Add Modal
   */
  const handleAddChange = (e) => {
    const { name, value, files } = e.target;
    setNewMember((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  /**
   * Handler for date changes in Add Modal
   */
  const handleAddDateChange = (gregorianDate, field) => {
    setNewMember((prev) => ({
      ...prev,
      [field]: gregorianDate,
    }));
  };

  // Combined Loading State
  const isLoadingCombined = isLoading || isLoadingDivisions;

  return (
    <NewLayoutWithHeader pageTitle="Member Renewal" steps={[]}>
      {isLoadingCombined ? (
        <LoadingIndicator />
      ) : isError || adminDivError ? (
        <div className="tw-text-center tw-text-red-500">
          Error: {error?.data?.message || adminDivError?.data?.message || 'Failed to load members or administrative divisions.'}
        </div>
      ) : (
        <div className="tw-p-4 tw-flex tw-bg-gray-100">
          <SidePanel 
            renewalProgress={renewalProgress}
            setRenewalProgress={setRenewalProgress}
            setDependents={setDependents}
            dependents={dependents} 
            administrativeDivisions={administrativeDivisions}
            selectedGote={selectedGote}
            setSelectedGote={setSelectedGote}
            selectedPremium={selectedPremium}
            setSelectedPremium={setSelectedPremium}
            selectedEnrollmentPeriod={selectedEnrollmentPeriod}
            setSelectedEnrollmentPeriod={setSelectedEnrollmentPeriod}
            householdEnrollmentRecord={householdEnrollmentRecord}
            refetch={refetch}
            householdHead={householdHead}
            memberEnrollmentRecords={data?.member_enrollment_records || []}
            selectedSecondWifeId={selectedSecondWifeId}
            setSelectedSecondWifeId={setSelectedSecondWifeId}
          />

          <div className="tw-w-full tw-ml-4 lg:tw-w-9/12 ">
            <HouseholdHead householdHead={householdHead} />
            <DependentsSection
              renewalProgress={renewalProgress} 
              dependents={dependents} 
              setDependents={setDependents}
              handleAddMember={handleAddMember} 
              updateMember={handleUpdateMember}
              selectedSecondWifeId={selectedSecondWifeId}
              setSelectedSecondWifeId={setSelectedSecondWifeId}
            />
          </div>
        </div>
      )}

      {/* Member Form Modal for Adding New Member */}
      <MemberFormModal
        showModal={isAdding}
        toggleModal={() => setIsAdding(false)}
        newMember={newMember}
        handleChange={handleAddChange}
        saveMember={handleCreateMember}
        isEditing={false}
        hasHouseholdHead={Boolean(householdHead)}
        handleDateChange={handleAddDateChange}
        errors={{}}
      />

      {/* Alert Modal */}
      {alert.message && (
        <CenteredModal onClose={() => setAlert({ message: null, type: null })}>
          {alert.type === 'alert' &&<ErrorAlert errors={[alert.message]} />}
          {alert.type === 'success' &&<SuccessAlert message={alert.message} />}
        </CenteredModal>
      )}
    </NewLayoutWithHeader>
  );
};

export default MemberRenewContainer;
